<template>
  <TableBody
    class="table-breakdown_body table-breakdown"
    :items="sortedItems"
    data-tst="breakdown_table_v2"
    style="--container-no-data-position: relative; --container-no-data-flex: inherit"
  >
    <template #no_data>
      <MessageOverlaySkeleton
        class="gw_mesblock_container"
        data-tst="breakdown_table_message"
        :isBlurred="true"
        :isTopDown="true"
        style="--back-from: var(--clr-black-0); --back-to: var(--clr-black-0); z-index: inherit"
      >
        <MessagesGoPostflop
          :namespace="namespace"
          :canAISolve="canAISolve"
          :posflopContent="$t('tabs.breakdown')"
        >
        </MessagesGoPostflop>
      </MessageOverlaySkeleton>
    </template>
    <template #head_sticky>
      <TableHead
        :sortConf="tbl_sortConf"
        :thead="thead"
        class="large-scroll-track"
        tstPrefix="breakdown_table"
        style="--z-index-head: inherit"
        @click:th="tbl_handleSort"
      >
        <template #th_breakdown_performed="v">
          <TableHeadCell
            :item="v.item"
            :sortConf="tbl_sortConf"
            tstPrefix="breakdown_performed"
            @click="tbl_handleSort"
          >
            <div
              class="csst"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'bottom' })"
            >
              {{ v.item.name }}
              <div
                class="csst_content text-small"
                style="position: fixed; max-width: 14rem"
              >
                {{ v.item.tooltip }}
              </div>
            </div>
          </TableHeadCell>
        </template>
        <template #th_breakdown_represented="v">
          <TableHeadCell
            :item="v.item"
            :sortConf="tbl_sortConf"
            tstPrefix="breakdown_represented"
            @click="tbl_handleSort"
          >
            <div
              class="csst"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'bottom' })"
            >
              {{ v.item.name }}
              <div
                class="csst_content text-small"
                style="position: fixed; max-width: 14rem"
              >
                {{ v.item.tooltip }}
              </div>
            </div>
          </TableHeadCell>
        </template>
        <template #th_breakdown_summary="v">
          <TableHeadCell
            :item="v.item"
            :sortConf="tbl_sortConf"
            tstPrefix="breakdown_summary"
            @click="tbl_handleSort"
          >
            <div
              class="csst"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'bottom' })"
            >
              {{ v.item.name }}
              <div
                class="csst_content text-small"
                style="position: fixed; max-width: 14rem"
              >
                {{ v.item.tooltip }}
              </div>
            </div>
          </TableHeadCell>
        </template>
      </TableHead>
    </template>
    <template #default="{ item, index }">
      <BreakdownTableV2Row
        class="table-breakdown_body_row"
        :class="[
          { 'table-breakdown_body_row_first': index === 0 },
          {
            'table-breakdown_body_row_last': index === sortedItems.length - 1,
          },
        ]"
        :thead="thead"
        :actionHighlighted="actionHighlighted"
        :activeHandsIds="activeHandsIds"
        :isPinned="isPinned"
        :item="item"
        :index="index"
        :useHex="useHex"
        :viewProps="viewProps"
        :showBetsIn="showBetsIn"
        :isScaled="index === scaledRowIdx"
        :isDimmed="scaledRowIdx !== null"
        @click="toggleScaledRow(index)"
        @combo:click="val => $emit('combo:click', val)"
        @combo:mouseenter="val => $emit('combo:mouseenter', val)"
        @combo:mouseleave="val => $emit('combo:mouseleave', val)"
        @allhands:mouseenter="val => $emit('allhands:mouseenter', val)"
      >
      </BreakdownTableV2Row>
    </template>
  </TableBody>
</template>

<script>
import MessagesGoPostflop from '@/components/Poker/Overlays/Messages/MessagesGoPostflop'
import MessageOverlaySkeleton from '@/components/Poker/Overlays/MessageOverlaySkeleton'
import { toggleCombos } from '@/helpers/solution'
import { getGroupedActions, getRaisesNonRaises } from '@/helpers/grouping'

import TableHead from '@/components/DataTables/v2/TableHead'
import TableHeadCell from '@/components/DataTables/v2/TableHeadCell'
import TableBody from '@/components/DataTables/v2/TableBody'
import BreakdownTableV2Row from '@/components/DataTables/v2/tables/BreakdownTableV2/BreakdownTableV2Row'

import float_mix from '@/mixins/float_mix'
import tier_mix from '@/mixins/tier_mix'
import table_mix from '@/components/DataTables/table_mix'

export default {
  name: 'BreakdownTableV2',
  components: {
    TableHead,
    TableHeadCell,
    TableBody,
    BreakdownTableV2Row,
    MessagesGoPostflop,
    MessageOverlaySkeleton,
  },
  mixins: [table_mix, float_mix, tier_mix],
  props: {
    namespace: { required: true, type: String },
    canAISolve: { required: true, type: Boolean },
    customCombos: Array,
    hero: Object,
    solution: Array,
    activeHandsIds: Array,
    isPinned: Boolean,
    showBetsIn: String,
    useHex: Boolean,
    grouping: String,
    filterCombos: Object,
    catDraws: Array,
    catHands: Array,
    catEqBuckets: Array,
    catEqBucketsAdvanced: Array,
    actionHighlighted: Object,
    viewProps: Object,
  },
  emits: ['combo:click', 'combo:mouseenter', 'combo:mouseleave', 'allhands:mouseenter'],
  data() {
    this.heroIndex = 0
    this.tbl_persistSortKey = 'sort_study_breakdown_table'
    this.l_sortConf = {
      sortKey: 'category',
      sortOrder: 'desc',
    }
    return {
      tbl_sortConf: this.l_sortConf,
      tooltipItem: { visible: false },
      // scaledRowIdx: 2,
      scaledRowIdx: null,
    }
  },
  methods: {
    toggleScaledRow(index) {
      this.scaledRowIdx = this.scaledRowIdx === index ? null : index
    },
    localSort({ tRows, sortKey, sortOrderDesc }) {
      let hands = tRows.filter(i => i.categoryId === 'hand_categories')
      let draws = tRows.filter(i => i.categoryId === 'draw_categories')

      let eqBuckets = tRows.filter(i => i.categoryId === 'equity_buckets')
      let eqBucketsAdvanced = tRows.filter(i => i.categoryId === 'equity_buckets_advanced')

      if (sortKey === 'category') {
        hands = hands.sort((a, b) =>
          a.display_order > b.display_order ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        draws = draws.sort((a, b) =>
          a.display_order > b.display_order ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        eqBuckets = eqBuckets.sort((a, b) =>
          a.display_order > b.display_order ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        eqBucketsAdvanced = eqBucketsAdvanced.sort((a, b) =>
          a.display_order > b.display_order ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
      } else if (sortKey === 'range_percent') {
        hands = hands.sort((a, b) =>
          a.totalRange < b.totalRange ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        draws = draws.sort((a, b) =>
          a.totalRange < b.totalRange ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        eqBuckets = eqBuckets.sort((a, b) =>
          a.totalRange < b.totalRange ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        eqBucketsAdvanced = eqBucketsAdvanced.sort((a, b) =>
          a.totalRange < b.totalRange ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
      } else if (sortKey === 'combos') {
        hands = hands.sort((a, b) =>
          a.totalCombos < b.totalCombos ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        draws = draws.sort((a, b) =>
          a.totalCombos < b.totalCombos ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        eqBuckets = eqBuckets.sort((a, b) =>
          a.totalCombos < b.totalCombos ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
        eqBucketsAdvanced = eqBucketsAdvanced.sort((a, b) =>
          a.totalCombos < b.totalCombos ? (sortOrderDesc ? 1 : -1) : sortOrderDesc ? -1 : 1,
        )
      } else if (sortKey === 'performed') {
        hands = hands.sort((a, b) =>
          a.raiseFoldCheckFreq < b.raiseFoldCheckFreq
            ? sortOrderDesc
              ? 1
              : -1
            : sortOrderDesc
              ? -1
              : 1,
        )
        draws = draws.sort((a, b) =>
          a.raiseFoldCheckFreq < b.raiseFoldCheckFreq
            ? sortOrderDesc
              ? 1
              : -1
            : sortOrderDesc
              ? -1
              : 1,
        )
        eqBuckets = eqBuckets.sort((a, b) =>
          a.raiseFoldCheckFreq < b.raiseFoldCheckFreq
            ? sortOrderDesc
              ? 1
              : -1
            : sortOrderDesc
              ? -1
              : 1,
        )
        eqBucketsAdvanced = eqBucketsAdvanced.sort((a, b) =>
          a.raiseFoldCheckFreq < b.raiseFoldCheckFreq
            ? sortOrderDesc
              ? 1
              : -1
            : sortOrderDesc
              ? -1
              : 1,
        )
      }

      const categories = [...hands, ...draws, ...eqBuckets, ...eqBucketsAdvanced]
      const firsTItemNames = [draws[0]?.name, eqBuckets[0]?.name, eqBucketsAdvanced[0]?.name]
      const res = categories.map(ca => ({
        ...ca,
        table_tr_css_class: firsTItemNames.includes(ca.name) ? 'tr-divider-top' : '',
      }))

      return res
    },
    getCategoriesByAction({ categories, solution, categoryId }) {
      if (this.filterCombos.actions.length) {
        solution = solution.filter(a => this.filterCombos.actions.some(fa => fa.code === a.code))
      }

      const res = categories.map(category => {
        const heroCategory = this.hero[categoryId].find(c => c.name === category.name)
        // at - actionTotals
        const totals = solution.reduce(
          (a, b) => ({
            atFrequencies: a.atFrequencies + heroCategory?.actions_total_frequencies[b.code],
          }),
          {
            atFrequencies: 0,
          },
        )
        const totalRange = heroCategory.total_frequency * totals.atFrequencies
        const totalCombos = totalRange * this.hero.total_combos
        const r = {
          ...category,
          totalRange,
          totalCombos,
        }
        return r
      })

      return res
    },
  },
  computed: {
    sortedItems() {
      const res = this.localSort({
        tRows: this.items,
        sortKey: this.tbl_sortConf.sortKey,
        sortOrderDesc: this.tbl_sortConf.sortOrder === 'desc',
      })
      return res
    },
    items() {
      let categories = [...this.hands, ...this.draws, ...this.eqBuckets, ...this.eqBucketsAdvanced]
      if (this.filterCombos.actions.length) {
        categories = categories.map(c => ({
          ...c,
          players: [
            {
              ...c.players[this.heroIndex],
              solution: c.players[this.heroIndex].solution.filter(a =>
                this.filterCombos.actions.some(fa => fa.code === a.code),
              ),
            },
            ...c.players.slice(this.heroIndex + 1),
          ],
        }))
      }

      let hands = []
      const { combos: cc } = toggleCombos({
        combos: this.customCombos,
        filter: this.filterCombos,
      })
      hands = cc.filter(c => c.players[this.heroIndex].range)

      // TODO would be better to do this on top level since this is then recomputed in <StudyAllHandsHero/> for all hands overview
      // so its happenig 2x times
      // fast enough for now thou
      if (this.filterCombos.actions.length) {
        hands = hands.map(c => ({
          ...c,
          solution: c.solution.filter(i => this.filterCombos.actions.some(a => a.code === i.code)),
        }))
      }

      categories = categories.map(ca => {
        const combos = hands.filter(c => ca.players[this.heroIndex].combos.includes(c.id))

        let raiseFoldCheckFreq = 0
        let solution = ca.players[this.heroIndex].solution || []

        const { raises, nonRaises } = getRaisesNonRaises({ actions: solution })
        if (solution.some(a => a.type === 'RAISE')) {
          raiseFoldCheckFreq = raises.reduce((a, b) => a + b.total_frequency, 0)
        } else {
          const foldCheck = nonRaises[0]
          raiseFoldCheckFreq = foldCheck?.total_frequency
        }

        if (this.grouping !== 'swv_grouping_none') {
          solution = getGroupedActions({
            actions: solution,
            grouping: this.grouping,
            groupValues: ['total_frequency'],
            raisesFirst: true,
          })
        }

        return {
          ...ca,
          solution,
          raiseFoldCheckFreq,
          summary: {
            combos,
          },
        }
      })
      return categories
    },
    hands() {
      const res = this.getCategoriesByAction({
        categories: this.catHands.filter(i => i.players[this.heroIndex].total_occurrence),
        solution: this.solution,
        categoryId: 'hand_categories',
      })
      return res
    },
    draws() {
      const res = this.getCategoriesByAction({
        categories: this.catDraws.filter(i => i.players[this.heroIndex].total_occurrence),
        solution: this.solution,
        categoryId: 'draw_categories',
      })
      return res
    },
    eqBuckets() {
      const res = this.getCategoriesByAction({
        categories: this.catEqBuckets.filter(i => i.players[this.heroIndex].total_occurrence),
        solution: this.solution,
        categoryId: 'equity_buckets',
      })
      return res
    },
    eqBucketsAdvanced() {
      const res = this.getCategoriesByAction({
        categories: this.catEqBucketsAdvanced.filter(
          i => i.players[this.heroIndex].total_occurrence,
        ),
        solution: this.solution,
        categoryId: 'equity_buckets_advanced',
      })
      return res
    },
    thead() {
      const styles1 = {
        cssStyle: '--min-width-th: 5.5rem; --max-width-th: 6rem',
        tdStyle: '--min-width-td: 5.5rem; --max-width-td: 6rem',
      }
      const styles2 = {
        cssStyle: '--min-width-th: 5.5rem; --max-width-th: 9rem',
        tdStyle: '--min-width-td: 5.5rem; --max-width-td: 9rem',
      }
      const res = {
        category: {
          name: this.$t('table_head.breakdown_category'),
          key: 'category',
          sortKey: 'category',
          cssStyle: '--min-width-th: 7rem; --max-width-th: 12rem',
          tdStyle: '--min-width-td: 7rem; --max-width-td: 12rem',
        },
        breakdown_performed: {
          name: this.$t('table_head.strategy'),
          key: 'breakdown_performed',
          sortKey: 'performed',
          tooltip: this.$t('tooltips.breakdown_performed'),
          ...styles2,
        },
        breakdown_range: {
          name: this.$t('table_head.breakdown_range'),
          key: 'breakdown_range',
          sortKey: 'range_percent',
          ...styles1,
        },
        combos: {
          name: this.$t('table_head.combos'),
          key: 'combos',
          sortKey: 'combos',
          ...styles1,
        },
        breakdown_summary: {
          name: this.$t('table_head.breakdown_summary'),
          key: 'breakdown_summary',
          tooltip: this.$t('tooltips.breakdown_summary'),
          cssStyle: '--min-width-th: 12rem',
          tdStyle: '--min-width-td: 12rem',
        },
      }
      return res
    },
  },
  watch: {
    thead: {
      immediate: true,
      handler: 'tbl_setSortConf',
    },
  },
}
</script>

<style lang="scss" scoped>
@media (hover: hover) and (pointer: fine) {
  .table-breakdown_body {
    &:hover {
      .table-breakdown_body_row {
        &_first {
          --hands-top-maximized: 0%;
        }
        &_last {
          --hands-top-maximized: -125%;
        }
      }
    }
  }
}
</style>
